import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Star } from 'lucide-react';
import { Button } from '../ui/button';
import { pricingPlans } from '../../data/mock';

const Pricing = () => {
  const [hoveredPlan, setHoveredPlan] = useState(null);

  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="pricing" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-cyan-500 text-sm font-semibold uppercase tracking-wider">Precios</span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-navy-800 mt-3 mb-5">
            Planes y Precios
          </h2>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto">
            Soluciones adaptadas a cada etapa de tu negocio. Sin costos ocultos.
          </p>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {pricingPlans.map((plan, index) => (
            <motion.div
              key={plan.id}
              className={`relative rounded-2xl p-8 transition-all ${
                plan.popular 
                  ? 'bg-cyan-500 text-white shadow-2xl shadow-cyan-500/30 scale-105 z-10' 
                  : 'bg-white border border-slate-200 hover:border-cyan-300 shadow-sm hover:shadow-lg'
              }`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.15 }}
              onMouseEnter={() => setHoveredPlan(plan.id)}
              onMouseLeave={() => setHoveredPlan(null)}
              whileHover={{ y: plan.popular ? 0 : -8 }}
            >
              {/* Popular Badge */}
              {plan.popular && (
                <motion.div
                  className="absolute -top-4 left-1/2 -translate-x-1/2 bg-white text-cyan-600 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 shadow-lg"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <Star className="w-3 h-3 fill-current" />
                  Recomendado
                </motion.div>
              )}

              {/* Plan Header */}
              <div className="text-center mb-8 pt-2">
                <h3 className={`text-lg font-semibold mb-2 ${plan.popular ? 'text-white' : 'text-navy-800'}`}>
                  {plan.name}
                </h3>
                <p className={`text-sm mb-6 ${plan.popular ? 'text-cyan-100' : 'text-slate-500'}`}>
                  {plan.description}
                </p>
                <div className="flex items-baseline justify-center gap-1">
                  <span className={`text-4xl font-bold ${plan.popular ? 'text-white' : 'text-navy-800'}`}>
                    {plan.price}
                  </span>
                </div>
              </div>

              {/* Features List */}
              <ul className="space-y-1 mb-8">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      plan.popular ? 'bg-white/20' : 'bg-cyan-50'
                    }`}>
                      <Check className={`w-3 h-3 ${plan.popular ? 'text-white' : 'text-cyan-600'}`} />
                    </span>
                    <span className={`text-sm ${plan.popular ? 'text-cyan-50' : 'text-slate-600'}`}>
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              <Button
                onClick={scrollToContact}
                className={`w-full py-3 font-semibold rounded-lg transition-all ${
                  plan.popular
                    ? 'bg-white text-cyan-600 hover:bg-cyan-50'
                    : 'bg-cyan-500 text-white hover:bg-cyan-600 shadow-lg shadow-cyan-500/25'
                }`}
              >
                Seleccionar Plan
              </Button>
            </motion.div>
          ))}
        </div>

        {/* Bottom Note */}
        <motion.p
          className="text-center text-slate-500 text-sm mt-12"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6 }}
        >
          Todos los precios incluyen IGV. Consulta por proyectos personalizados.
        </motion.p>
      </div>
    </section>
  );
};

export default Pricing;
